import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Toolbar,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Formik, Form } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { apiClient } from "../api/client";
import { ISchoolContactServiceResponse } from "../api/PartnerPortalGatewayApi";
import { CACard } from "../components/ui";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setAvailableServices, setLoading, setSelectedService } from "../store/slices/serviceSlice";

const validationSchema = Yup.object().shape({
  selectedServiceId: Yup.string().required("Please select a service"),
});

// Key for storing serviceId in sessionStorage for cross-redirect persistence
const SERVICE_ID_STORAGE_KEY = "pending_service_id";

export const ServiceSelector: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedService = useAppSelector((state: any) => state.service.selectedService);
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceNotFoundError, setServiceNotFoundError] = useState<string | null>(null);
  
  // Track if we've processed the serviceId to prevent re-processing
  const hasProcessedServiceId = React.useRef(false);
  
  // Get serviceId from URL params or sessionStorage (read once on mount)
  const serviceIdFromUrl = searchParams.get("serviceId");
  const initialServiceId = React.useMemo(() => {
    return serviceIdFromUrl ?? sessionStorage.getItem(SERVICE_ID_STORAGE_KEY);
  }, []); // Only compute once on mount

  // Use React Query to load services
  const {
    data: services = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["school-contact-services"],
    queryFn: async () => {
      // Get services for the authenticated contact
      const schoolContactServices = await apiClient.schoolContact.listServicesForContact();

      if (!schoolContactServices || schoolContactServices.length === 0) {
        return [];
      }

      // Filter to only contacts with serviceId
      const validServices = schoolContactServices.filter((contact) => contact.serviceId);

      // Remove duplicates by service ID
      const uniqueServices = validServices.filter(
        (service, index, self) => self.findIndex((s) => s.serviceId === service.serviceId) === index,
      );

      // Sort by service name
      const sortedServices = uniqueServices.sort((a, b) => (a.serviceName ?? "").localeCompare(b.serviceName ?? ""));

      // Convert class instances to plain objects for Redux serialization
      return sortedServices.map((service) => service.toJSON());
    },
    staleTime: 0,
    gcTime: 0,
  });

  // Handle side effects when services data changes
  React.useEffect(() => {
    if (services) {
      dispatch(setAvailableServices(services));
      dispatch(setLoading(false));

      // Auto-select if only one service (but not if there's a pending serviceId to process)
      if (services.length === 1 && !initialServiceId) {
        dispatch(setSelectedService(services[0]));
      }
    }
  }, [services, dispatch, initialServiceId]);

  // Handle loading state in Redux
  React.useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading, dispatch]);

  const hasServices = services && services.length > 0;

  // If user already has a selected service and it's valid, redirect to dashboard
  // But skip this redirect if there's a pending serviceId to process or an error message showing
  React.useEffect(() => {
    // Don't redirect if we have a serviceId to process and haven't processed it yet
    if (initialServiceId && !hasProcessedServiceId.current) {
      return;
    }
    // Don't redirect if there's an error to show
    if (serviceNotFoundError) {
      return;
    }
    if (selectedService && services.some((s) => s.serviceId === selectedService.serviceId)) {
      navigate("/dashboard", { replace: true });
    }
  }, [selectedService, services, navigate, initialServiceId, serviceNotFoundError]);

  // Handle serviceId query parameter for auto-selection
  React.useEffect(() => {
    // Skip if no serviceId to process, or already processed, or services not loaded
    if (!initialServiceId || hasProcessedServiceId.current || services.length === 0) {
      return;
    }

    // Mark as processed immediately to prevent re-runs
    hasProcessedServiceId.current = true;

    const requestedServiceId = parseInt(initialServiceId, 10);

    // Clear the serviceId from URL query string if present
    if (serviceIdFromUrl) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("serviceId");
      setSearchParams(newSearchParams, { replace: true });
    }
    
    // Always clear sessionStorage
    sessionStorage.removeItem(SERVICE_ID_STORAGE_KEY);

    if (isNaN(requestedServiceId)) {
      setServiceNotFoundError("Invalid service ID provided in the URL.");
      return;
    }

    const matchingService = services.find((s) => s.serviceId === requestedServiceId);

    if (matchingService) {
      dispatch(setSelectedService(matchingService));
      navigate("/dashboard", { replace: true });
    } else {
      setServiceNotFoundError(
        "The requested service is not accessible. You may not have permission to access this service, or it may no longer exist.",
      );
    }
  }, [initialServiceId, serviceIdFromUrl, services, dispatch, navigate, searchParams, setSearchParams]);

  const handleSubmit = (values: { selectedServiceId: string }) => {
    const service = services.find((s) => s.serviceId?.toString() === values.selectedServiceId);
    if (service) {
      dispatch(setSelectedService(service));
      navigate("/dashboard", { replace: true });
    }
  };

  const handleSignOut = () => {
    navigate("/logout");
  };

  if (isLoading) {
    return (
      <>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Partner Portal
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="md" sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading your services...
          </Typography>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Partner Portal
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="md" sx={{ mt: 4 }}>
          <CACard>
            <Box sx={{ textAlign: "center", padding: 2 }}>
              <Typography variant="h5" color="error" gutterBottom>
                Access Denied
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                You do not have permissions to view service data.
              </Alert>
              <Box display="flex" gap={2} justifyContent="center">
                <Button variant="outlined" onClick={() => window.location.reload()}>
                  Try Again
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleSignOut}>
                  Sign Out
                </Button>
              </Box>
            </Box>
          </CACard>
        </Container>
      </>
    );
  }

  if (!hasServices) {
    return (
      <>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Partner Portal
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="md" sx={{ mt: 4 }}>
          <CACard>
            <Box sx={{ textAlign: "center", padding: 2 }}>
              <Typography variant="h5" gutterBottom>
                No Services Found
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                It appears you don't have access to any services. Please contact your administrator or support team to
                get access.
              </Typography>
              <Button variant="outlined" color="secondary" onClick={handleSignOut} fullWidth>
                Sign Out
              </Button>
            </Box>
          </CACard>
        </Container>
      </>
    );
  }

  // Multiple services - show selection interface
  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Partner Portal
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CACard>
          <Box sx={{ padding: 2 }}>
            <Typography variant="h4" gutterBottom align="center">
              Select Your Service
            </Typography>
            <Typography variant="body1" paragraph align="center" color="textSecondary">
              You can change this at any time via settings.
            </Typography>

            {serviceNotFoundError && (
              <Alert
                severity="warning"
                sx={{ mb: 3 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setServiceNotFoundError(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {serviceNotFoundError}
              </Alert>
            )}

            <Formik
              initialValues={{ selectedServiceId: "" }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, touched, errors, handleChange, isValid, dirty }) => (
                <Form>
                  <FormControl
                    fullWidth
                    error={touched.selectedServiceId && Boolean(errors.selectedServiceId)}
                    sx={{ mb: 3 }}
                  >
                    <InputLabel id="service-select-label">Service</InputLabel>
                    <Select
                      labelId="service-select-label"
                      id="selectedServiceId"
                      name="selectedServiceId"
                      value={values.selectedServiceId}
                      label="Service"
                      onChange={handleChange}
                    >
                      {services.map((service: ISchoolContactServiceResponse) => (
                        <MenuItem key={service.serviceId} value={service.serviceId?.toString()}>
                          {service.serviceName}
                          {service.positionName && ` - ${service.positionName}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.selectedServiceId && errors.selectedServiceId && (
                      <FormHelperText>{errors.selectedServiceId}</FormHelperText>
                    )}
                  </FormControl>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Button type="submit" variant="contained" size="large" disabled={!isValid || !dirty}>
                      Confirm &amp; Continue
                    </Button>
                    <Button variant="outlined" onClick={handleSignOut}>
                      Sign Out
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </CACard>
      </Container>
    </>
  );
};

export default ServiceSelector;
